import {NgClass} from '@angular/common';
import {AbstractControl, ValidatorFn} from '@angular/forms';
import {DateAdapter, MAT_DATE_FORMATS, MatDateFormats, MatNativeDateModule} from '@angular/material/core';
import {MatCalendar, MatDatepickerModule, MatDateRangePicker} from '@angular/material/datepicker';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatInputModule} from '@angular/material/input';
import moment from 'moment-timezone';
import {Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {FieldUiDataComboBoxObj, FieldUiDataObj} from '../../../../../../../generated/generated-core.model';
import {MxMaterialModule} from '../../../../../../material/mx-material.module';
import {MxFormErrorMessageService} from '../../../../services/mx-form-error-message.service';
import {MxField} from '../../services/mxField';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    Inject,
    Injectable,
    Input,
    OnDestroy,
    OnInit
} from '@angular/core';
import {MX_TRANSLATE_SERVICE_TOKEN, MxTranslateService} from '../../../../../../shared/services/mx-translate.service';
import {BreakpointObserver, Breakpoints} from '@angular/cdk/layout';
import {MxSharedModule} from '../../../../../../shared/mx-shared.module';

interface DateRangeItem {
    key: string;
    description: string;
    start: moment.Moment;
    end: moment.Moment;
}

@Injectable()
class DateRangePickerRangesService {

    private readonly RANGES: DateRangeItem[] = [
        {key: 'today', description: 'label.stats.date.range.today',
            start: moment(),
            end: moment()},
        {key: 'yesterday', description: 'label.stats.date.range.yesterday',
            start: moment().subtract(1, 'days'),
            end: moment().subtract(1, 'days')},
        {key: 'currweek', description: 'label.stats.date.range.currweek',
            start: moment().startOf('week'),
            end: moment()},
        {key: 'prevweek', description: 'label.stats.date.range.prevweek',
            start: moment().startOf('week').subtract(1, 'weeks'),
            end: moment().startOf('week').subtract(1, 'days')},
        {key: 'prev7days', description: 'label.stats.date.range.prev.7.days',
            start: moment().subtract(7, 'days'),
            end: moment().subtract(1, 'days')},
        {key: 'currmonth', description: 'label.stats.date.range.currmonth',
            start: moment().startOf('month'),
            end: moment()},
        {key: 'prevmonth', description: 'label.stats.date.range.prevmonth',
            start: moment().startOf('month').subtract(1, 'months'),
            end: moment().startOf('month').subtract(1, 'days')},
        {key: 'prev30days', description: 'label.stats.date.range.prev.30.days',
            start: moment().subtract(30, 'days'),
            end: moment().subtract(1, 'days')},
        {key: 'threemonths', description: 'label.stats.date.range.threemonths',
            start: moment().subtract(3, 'months'),
            end: moment()},
        {key: 'prev90days', description: 'label.stats.date.range.prev.90.days',
            start: moment().subtract(90, 'days'),
            end: moment().subtract(1, 'days')},
        {key: 'sixmonths', description: 'label.stats.date.range.sixmonths',
            start: moment().subtract(6, 'months'),
            end: moment()},
        {key: 'prev180days', description: 'label.stats.date.range.prev.180.days',
            start: moment().subtract(180, 'days'),
            end: moment().subtract(1, 'days')},
        {key: 'curryear', description: 'label.stats.date.range.curryear',
            start: moment().startOf('year'),
            end: moment()},
        {key: 'prevyear', description: 'label.stats.date.range.prevyear',
            start: moment().startOf('year').subtract(1, 'years'),
            end: moment().startOf('year').subtract(1, 'days')},
        {key: 'prev365days', description: 'label.stats.date.range.prev.365.days',
            start: moment().subtract(365, 'days'),
            end: moment().subtract(1, 'days')}
    ];
    private rangeFilter: string[];
    private listUpdateHandler: (value: string) => void;
    private listType: string;

    public setRangeFilter(rangeFilter: string[]) {
        this.rangeFilter = rangeFilter;
    }

    public getRange(full: boolean = false): DateRangeItem[] {
        if (this.rangeFilter && !full) {
            return this.RANGES.filter(value => this.rangeFilter.indexOf(value.key) > -1);
        } else {
            return this.RANGES;
        }
    }

    public setDateRangeListHandler(initValue: string, updateHandler: (value: string) => void): void {
        this.listType = initValue;
        this.listUpdateHandler = updateHandler;
    }

    public getInitListValue(): string {
        return this.listType;
    }

    public updateList(value: string): void {
        this.listType = value;
        this.listUpdateHandler(value);
    }

}
@Component({
    selector: 'mx-date-range-header',
    templateUrl: 'mx-date-range-header.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    styleUrls: ['./mx-date-range.component.scss'],
    imports: [MatFormFieldModule, MatInputModule, MatNativeDateModule, MatDatepickerModule, MxMaterialModule, NgClass, MxSharedModule]
})
export class DateRangeHeaderComponent<D> implements OnDestroy {

    listType: string;

    private readonly destroyed$ = new Subject<void>();

    constructor(
        public rangeService: DateRangePickerRangesService,
        private calendar: MatCalendar<D>,
        private dateAdapter: DateAdapter<D>,
        private picker: MatDateRangePicker<D>,
        @Inject(MAT_DATE_FORMATS) private dateFormats: MatDateFormats,
        @Inject(MX_TRANSLATE_SERVICE_TOKEN) private translateService: MxTranslateService,
        cdr: ChangeDetectorRef,
    ) {
        calendar.stateChanges.pipe(takeUntil(this.destroyed$))
            .subscribe(() => cdr.markForCheck());
        calendar.selectedChange.pipe(takeUntil(this.destroyed$))
            .subscribe(() => this.changeListType('custom'));
        this.translateService.translationsChanged$
            ?.pipe(takeUntil(this.destroyed$))
            .subscribe(() => cdr.markForCheck());
        this.listType = this.rangeService.getInitListValue();
    }

    get periodLabel(): string {
        return this.dateAdapter
            .format(this.calendar.activeDate, this.dateFormats.display.monthYearLabel)
            .toLocaleUpperCase();
    }

    ngOnDestroy(): void {
        this.destroyed$.next();
        this.destroyed$.complete();
    }

    translate(value: string): string {
        return this.translateService.getTranslatedLabel(value);
    }

    previousClicked(mode: 'month' | 'year'): void {
        this.changeDate(mode, -1);
    }

    nextClicked(mode: 'month' | 'year'): void {
        this.changeDate(mode, 1);
    }

    selectRange(item: DateRangeItem): void {
        this.changeListType(item.key);
        this.picker.select(item.start as unknown as D);
        this.picker.select(item.end as unknown as D);
        this.picker.close();
    }

    changeListType(value: string): void {
        this.listType = value;
        this.rangeService.updateList(value);
    }

    private changeDate(mode: 'month' | 'year', amount: -1 | 1): void {
        this.calendar.activeDate =
            mode === 'month'
                ? this.dateAdapter.addCalendarMonths(this.calendar.activeDate, amount)
                : this.dateAdapter.addCalendarYears(this.calendar.activeDate, amount);
    }
}

@Component({
    selector: 'mx-date-range',
    standalone: false,
    templateUrl: './mx-date-range.component.html',
    styleUrls: ['./mx-date-range.component.scss'],
    providers: [DateRangePickerRangesService]
})
export class MxDateRangeComponent extends MxField implements OnInit {
    @Input() fieldName: string;
    @Input() dateRangeParams: Array<FieldUiDataObj>;
    @Input() validators: Array<ValidatorFn> = [];

    readonly dateRangeHeaderComponent = DateRangeHeaderComponent;

    showClearButton: boolean = true;
    listDateRangeFormControl: AbstractControl;
    startDateFormControl: AbstractControl;

    protected isMobile: boolean = false;
    private endDateFormControl: AbstractControl;

    constructor(
                private readonly breakpointObserver: BreakpointObserver,
                private readonly rangeService: DateRangePickerRangesService,
                protected readonly errorService: MxFormErrorMessageService,
                @Inject(MX_TRANSLATE_SERVICE_TOKEN) protected translateService: MxTranslateService) {
        super(errorService, translateService);
    }

    ngOnInit(): void {
        this.breakpointObserver.observe([Breakpoints.Handset])
            .subscribe(result => {
                this.isMobile = result.matches;
            });

        if (this.fieldConfig && this.fieldConfig.showClearButton === false) {
            this.showClearButton = false;
        }

        this.startDateFormControl = this.form.controls[this.fieldName + '_from'];
        this.endDateFormControl = this.form.controls[this.fieldName + '_to'];
        this.listDateRangeFormControl = this.form.controls[this.fieldName + '_list'];
        const initValue = this.rangeService.getRange(true)
            .find(value => value.key === this.listDateRangeFormControl.value);
        if (initValue) {
            this.startDateFormControl.setValue(initValue.start);
            this.endDateFormControl.setValue(initValue.end);
        }

        const [listField] = this.dateRangeParams;
        const options: Array<{key: string}> = (<FieldUiDataComboBoxObj>listField).options;
        const availableListOptions = options.map(value => value.key);
        this.rangeService.setRangeFilter(availableListOptions);
        this.rangeService.setDateRangeListHandler(this.listDateRangeFormControl.value as string,
                value => this.listDateRangeFormControl.setValue(value));
    }

    resetValue(): void {
        this.listDateRangeFormControl.reset(null, {emitEvent: false});
        this.listDateRangeFormControl.markAsTouched();
        this.listDateRangeFormControl.markAsDirty();
        this.startDateFormControl.reset(null, {emitEvent: false});
        this.startDateFormControl.markAsTouched();
        this.startDateFormControl.markAsDirty();
        this.endDateFormControl.reset(null, {emitEvent: false});
        this.endDateFormControl.markAsTouched();
        this.endDateFormControl.markAsDirty();
    }

    getValidationMessages = (): string[] => {
        if (!this.form) {
            return;
        }

        return this.errorService.getMessages(this.fieldName + '_from', this.form);
    };

    endDateChanged() {
        if (!this.endDateFormControl.value) {
            this.endDateFormControl.setValue(this.startDateFormControl.value);
        }
    }
}
